import * as React from "react"
import * as RechartsPrimitive from "recharts"
import styled, { useTheme } from "styled-components"

export type ChartConfig = {
  [k in string]: {
    label?: React.ReactNode
    icon?: React.ComponentType
    color?: string
  }
}

type ChartContextProps = {
  config: ChartConfig
}

const ChartContext = React.createContext<ChartContextProps | null>(null)

function useChart() {
  const context = React.useContext(ChartContext)
  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />")
  }
  return context
}

const ChartContainerRoot = styled.div`
  display: flex;
  aspect-ratio: 16/9;
  justify-content: center;
  text-align: center;
  width: 100%;
`

const ChartContainer = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    config: ChartConfig
    children: React.ComponentProps<
      typeof RechartsPrimitive.ResponsiveContainer
    >["children"]
  }
>(({ id, config, children, ...props }, ref) => {
  const uniqueId = React.useId()
  const chartId = `chart-${id || uniqueId.replace(/:/g, "")}`

  return (
    <ChartContext.Provider value={{ config }}>
      <ChartContainerRoot
        data-chart={chartId}
        ref={ref}
        {...props}
      >
        <RechartsPrimitive.ResponsiveContainer>
          {children}
        </RechartsPrimitive.ResponsiveContainer>
      </ChartContainerRoot>
    </ChartContext.Provider>
  )
})
ChartContainer.displayName = "ChartContainer"

const TooltipContent = styled.div`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.md};
  padding: ${props => props.theme.spacing[3]};
  box-shadow: ${props => props.theme.shadows.lg};
`

const TooltipLabel = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing[2]} 0;
`

const TooltipItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
`

const TooltipIndicator = styled.div<{ $color?: string }>`
  width: 8px;
  height: 8px;
  border-radius: ${props => props.theme.borders.radius.full};
  background: ${props => props.$color || props.theme.colors.primary};
`

const ChartTooltip = RechartsPrimitive.Tooltip

const ChartTooltipContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof RechartsPrimitive.Tooltip> &
    React.ComponentProps<"div"> & {
      hideLabel?: boolean
      hideIndicator?: boolean
      indicator?: "line" | "dot" | "dashed"
      nameKey?: string
      labelKey?: string
    }
>(
  (
    {
      active,
      payload,
      hideLabel = false,
      hideIndicator = false,
      indicator = "dot",
      label,
      labelFormatter,
      labelClassName,
      formatter,
      nameKey,
      labelKey,
    },
    ref
  ) => {
    const { config } = useChart()
    const theme = useTheme()

    if (!active || !payload?.length) {
      return null
    }

    const items = payload.map((item: any) => {
      const key = `${nameKey || item.name || item.dataKey || "value"}`
      const itemConfig = config[key as keyof typeof config]
      const value = item.value

      // Se o nome for 'value', troca para 'Quantidade'
      let labelName = itemConfig?.label || item.name || key;
      if (labelName === 'value') labelName = 'Quantidade';

      return {
        name: labelName,
        value: formatter ? formatter(value, item.name, item, 0, payload) : value.toLocaleString(),
        color: item.payload.fill || item.color || itemConfig?.color || theme.colors.primary,
      }
    })

    return (
      <TooltipContent ref={ref}>
        {!hideLabel && (
          <TooltipLabel>
            {labelFormatter ? labelFormatter(label, payload) : label}
          </TooltipLabel>
        )}
        {items.map((item, index) => (
          <TooltipItem key={index}>
            {!hideIndicator && (
              <TooltipIndicator $color={item.color} />
            )}
            <span>{item.name}:</span>
            <span style={{ fontWeight: 600, color: theme.colors.textPrimary }}>
              {item.value}
            </span>
          </TooltipItem>
        ))}
      </TooltipContent>
    )
  }
)
ChartTooltipContent.displayName = "ChartTooltipContent"

export { ChartContainer, ChartTooltip, ChartTooltipContent }
